// VIDEO-EDITING — runtime types + Zod schemas for a video project.
//
// The Django API at `/api/videos/projects/{id}/` returns these shapes; the
// schemas exist so we Zod-validate at the network boundary (per
// frontend/CLAUDE.md Gotcha #2) and so the Zustand persist `merge`
// rehydrates drafts safely.
import { z } from "zod";
// VIDEO-EDITING — canonical Clip type owned by core's Remotion-layers
// package. We re-export it so consumers don't depend on the alias path,
// and so the Zod schema below stays a loose runtime check (the strict
// validator on the trust boundary lives server-side at
// core/src/platform/providers/video-edit/spec.ts).
import {
  CLIP_TRANSITION_KINDS,
  type BrollClip as RemotionBrollClip,
  type CaptionPresetId as RemotionCaptionPresetId,
  Clip as RemotionClip,
  VideoEditSpec as RemotionVideoEditSpec,
} from "@wiser/remotion-layers";

import {
  OverlaySchema,
  OverlayTranslationSchema,
} from "./overlays";
import { CustomCaptionStyleSchema } from "./presets";

export type Clip = RemotionClip;
export type Broll = RemotionBrollClip;
export type VideoEditSpec = RemotionVideoEditSpec;
export type CaptionPresetId = RemotionCaptionPresetId;

// VIDEO-EDITING-MVP — synthetic "active language" id for the source
// transcript in the language switcher. Distinct from any BCP-47 code.
export const SOURCE_LANGUAGE_ID = "__source__";

export const TranscriptWordSchema = z.object({
  start: z.number(),
  end: z.number(),
  word: z.string(),
});

export const TranscriptSegmentSchema = z.object({
  start: z.number(),
  end: z.number(),
  text: z.string(),
  words: z.array(TranscriptWordSchema).optional(),
});

export const TranscriptVisualsSchema = z.object({
  onScreenText: z.array(
    z.object({
      start: z.number(),
      end: z.number(),
      text: z.string(),
      kind: z.enum([
        "overlay-hook",
        "caption",
        "sticker",
        "watermark",
        "product-label",
      ]),
      position: z
        .enum(["top", "middle", "bottom", "center", "corner"])
        .optional(),
    }),
  ),
  visualBeats: z.array(
    z.object({
      start: z.number(),
      end: z.number(),
      sceneDescription: z.string(),
      framing: z.string(),
      environment: z.string(),
      creatorAction: z.string(),
      productPresence: z.enum(["none", "entering", "hero", "background"]),
      bRoll: z.string().optional(),
    }),
  ),
  durationSeconds: z.number(),
  aspectRatio: z.enum(["9:16", "1:1", "16:9", "other"]).optional(),
});

export const TranscriptSchema = z.object({
  language: z.string(),
  segments: z.array(TranscriptSegmentSchema),
  rawProvider: z.string().optional(),
  visuals: TranscriptVisualsSchema.optional(),
});

export const TranslationEntrySchema = z.object({
  language: z.string(),
  segments: z.array(TranscriptSegmentSchema),
  rawProvider: z.string().optional(),
  createdAt: z.string().optional(),
});

export const ExportRecordSchema = z.object({
  url: z.string().optional(),
  format: z.string().optional(),
  exportKey: z.string().optional(),
  filename: z.string().optional(),
  createdAt: z.string().optional(),
  presetId: z.string().optional(),
  language: z.string().optional(),
  sizeBytes: z.number().optional(),
});

// VIDEO-EDITING — b-roll ripple-insert timeline (ordered trimmed clips).
// We keep the runtime schema loose (passthrough) and let TypeScript trust
// the canonical Clip type from @wiser/remotion-layers — the authoritative
// runtime validator lives server-side at core/src/platform/providers/
// video-edit/spec.ts.
export const ClipSchema: z.ZodType<Clip> = z
  .object({
    id: z.string(),
    kind: z.enum(["source", "broll", "still"]),
    url: z.string(),
    storageKey: z.string(),
    mimeType: z.string(),
    trimStartSec: z.number(),
    trimEndSec: z.number(),
    muted: z.boolean(),
    volume: z.number().optional(),
    sourceAudioMode: z.enum(["none", "continue"]).optional(),
    sourceAudioVolume: z.number().optional(),
    assetDurationSec: z.number().optional(),
    transitionInSec: z.number().optional(),
    transitionInKind: z.enum(CLIP_TRANSITION_KINDS).optional(),
  });

// VIDEO-EDITING — b-roll OVERLAY layer (cutaways rendered on top of the
// source for [startSec, endSec], never splicing it). Loose runtime schema
// like ClipSchema; the strict validator lives server-side.
export const BrollSchema: z.ZodType<Broll> = z.object({
  id: z.string(),
  url: z.string(),
  storageKey: z.string(),
  mimeType: z.string(),
  startSec: z.number(),
  endSec: z.number(),
  trimStartSec: z.number(),
  trimEndSec: z.number(),
  assetDurationSec: z.number().optional(),
  muted: z.boolean(),
  volume: z.number().optional(),
  transitionSec: z.number().optional(),
  transitionKind: z.enum(CLIP_TRANSITION_KINDS).optional(),
  opacity: z.number().optional(),
  source: z
    .object({
      kind: z.enum(["stock", "generative", "upload"]).optional(),
      actionId: z.string().optional(),
      proposalId: z.string().optional(),
      aiPrompt: z.string().optional(),
      provider: z.string().optional(),
      searchQuery: z.string().optional(),
      attributionLine: z.string().nullable().optional(),
    })
    .optional(),
});

export const SourceTimelineVisualBeatSchema = z.object({
  startSec: z.number(),
  endSec: z.number(),
  label: z.string().optional(),
  description: z.string().optional(),
  thumbnailUrl: z.string().optional(),
});

export const SourceTimelineShotTrackSchema = z.object({
  startSec: z.number(),
  endSec: z.number(),
  label: z.string(),
  keyFrameUrls: z.array(z.string()).optional(),
});

export const SourceTimelineSceneChangeSchema = z.object({
  timeSec: z.number(),
  frameUrl: z.string().optional(),
});

export const SourceTimelineSchema = z.object({
  visualBeats: z.array(SourceTimelineVisualBeatSchema).default([]),
  shotTracks: z.array(SourceTimelineShotTrackSchema).default([]),
  sceneChanges: z.array(SourceTimelineSceneChangeSchema).default([]),
});

// VIDEO-EDITING — background-music selection. A single royalty-free track
// laid under the whole video; mixed in at export by the Core pipeline. Loose
// runtime schema like the others; the strict validator lives server-side at
// core/src/platform/providers/video-edit/spec.ts (`MusicSpecSchema`).
export const ProjectMusicSchema = z.object({
  trackId: z.string(),
  trackUrl: z.string(),
  /** Music gain in dB relative to dialogue (≤ 0). Defaults to -18. */
  gainDb: z.number(),
});

export type ProjectMusic = z.infer<typeof ProjectMusicSchema>;

export const VideoProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string().nullable(),
  workspaceId: z.string().nullable(),
  videoUrl: z.string().nullable(),
  videoStorageKey: z.string().nullable(),
  originalFilename: z.string().nullable(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  transcript: TranscriptSchema.nullable(),
  presetId: z.string().nullable(),
  customStyle: CustomCaptionStyleSchema.nullable(),
  exports: z.array(ExportRecordSchema),
  originalTranscript: TranscriptSchema.nullable(),
  translations: z.array(TranslationEntrySchema),
  overlays: z.array(OverlaySchema),
  overlayTranslations: z.array(OverlayTranslationSchema),
  clips: z.array(ClipSchema).optional(),
  brolls: z.array(BrollSchema).optional(),
  music: ProjectMusicSchema.nullable().optional(),
  sourceTimeline: SourceTimelineSchema.nullable().optional(),
  // True when the source mp4 already has captions baked into its
  // pixels (UGC pipelines run `burn-captions` before persisting).
  // Drives two suppressions: the preview's CaptionOverlay short-circuits
  // so the user doesn't see two stacked caption layers, and the MP4
  // export skips its ASS subtitle pass so the output isn't double-burned.
  captionsBurnedIn: z.boolean().optional(),
});

export type TranscriptWord = z.infer<typeof TranscriptWordSchema>;
export type TranscriptSegment = z.infer<typeof TranscriptSegmentSchema>;
export type TranscriptVisuals = z.infer<typeof TranscriptVisualsSchema>;
export type Transcript = z.infer<typeof TranscriptSchema>;
export type TranslationEntry = z.infer<typeof TranslationEntrySchema>;
export type ExportRecord = z.infer<typeof ExportRecordSchema>;
export type SourceTimelineVisualBeat = z.infer<typeof SourceTimelineVisualBeatSchema>;
export type SourceTimelineShotTrack = z.infer<typeof SourceTimelineShotTrackSchema>;
export type SourceTimelineSceneChange = z.infer<typeof SourceTimelineSceneChangeSchema>;
export type SourceTimeline = z.infer<typeof SourceTimelineSchema>;
export type VideoProject = z.infer<typeof VideoProjectSchema>;

// Languages that flip the transcript pane + overlay text direction.
const RTL_LANGS = new Set([
  "fa",
  "ar",
  "he",
  "ur",
  "ps",
  "sd",
  "fa-ir",
  "ar-sa",
  "he-il",
]);

export const RTL_FONT_STACK =
  "'Vazirmatn', 'Cairo', 'Noto Sans Hebrew', 'Inter', system-ui, sans-serif";

export function isRtl(language: string | undefined): boolean {
  if (!language) return false;
  const code = language.toLowerCase();
  if (RTL_LANGS.has(code)) return true;
  const base = code.split("-")[0];
  return RTL_LANGS.has(base);
}
